import { isDefined } from "@clipboard-health/util-ts";
import { createIndependentContractorBadgePdf } from "@src/appV2/Agents/components/ContractorBadge/utils/createBadgePdf";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";

export function useGetBadgePdf(params: {
  licenseNumber: string | undefined;
  qualification: string | undefined;
  nameDetails:
    | {
        firstName: string;
        lastName: string;
      }
    | undefined;
  badgePicture: string | undefined;
  workerId: string | undefined;
}) {
  return useQuery<{ blob: Blob; pdfKey: string }, Error>({
    queryKey: [
      "independent-contractor-badge",
      params.licenseNumber,
      params.qualification,
      params.nameDetails?.firstName,
      params.nameDetails?.lastName,
      params.badgePicture,
    ],
    queryFn: async () => {
      const { licenseNumber, qualification, badgePicture, nameDetails } = params;

      if (!nameDetails?.firstName || !nameDetails?.lastName) {
        logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
          error: new Error("This worker doesn't have a name."),
        });
        throw new Error("This worker doesn't have a name.");
      }

      if (!isDefined(qualification)) {
        logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
          error: new Error("This worker doesn't have a qualification."),
        });

        throw new Error("This worker doesn't have a qualification.");
      }

      if (!isDefined(badgePicture)) {
        logError(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_GENERATION_FAILURE, {
          error: new Error("This worker doesn't have a profile picture."),
        });
        throw new Error("This worker doesn't have a profile picture.");
      }

      return await createIndependentContractorBadgePdf({
        details: {
          firstName: nameDetails.firstName ?? "",
          lastName: nameDetails.lastName ?? "",
          qualification,
          licenseNumber,
          profilePictureBase64: badgePicture,
        },
      });
    },

    // we need to run it only when the required params are defined
    enabled:
      isDefined(params.nameDetails) &&
      isDefined(params.qualification) &&
      isDefined(params.badgePicture),

    refetchOnWindowFocus: false,
  });
}
